/**
 * House of Racket: Estimated Delivery Date Checker Component
 * Handles PIN validation, API integration, state rendering, and caching.
 */
(() => {
  const STORAGE_KEY = 'hor_delivery_pincode';
  const CLIENT_CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

  class HorDeliveryChecker extends HTMLElement {
    constructor() {
      super();
      this.inputEl = null;
      this.buttonEl = null;
      this.resultContainer = null;
      this.formEl = null;
      this.subtextEl = null;
      this.currentPincode = '';
    }

    connectedCallback() {
      this.inputEl = this.querySelector('[data-hor-pincode-input]');
      this.buttonEl = this.querySelector('[data-hor-check-button]');
      this.resultContainer = this.querySelector('[data-hor-result-container]');
      this.formEl = this.querySelector('[data-hor-form]');
      this.subtextEl = this.querySelector('[data-hor-subtext]');

      if (!this.inputEl || !this.buttonEl || !this.resultContainer) {
        return;
      }

      this.initEvents();
      this.loadSavedPincode();
    }

    getProductFirstWord() {
      const currentTitle =
        this.dataset.productTitle ||
        document.querySelector('.product__title h1, .product__title')?.textContent?.trim() ||
        '';
      const cleanTitle = currentTitle.trim().replace(/^["'\[\]\s]+/, '').toLowerCase();
      let firstWord = cleanTitle.split(/\s+/)[0] || 'default';
      if (cleanTitle.startsWith('li ning')) {
        firstWord = 'li-ning';
      }
      return firstWord;
    }

    initEvents() {
      // Handle button click & form submit
      this.buttonEl.addEventListener('click', (e) => {
        e.preventDefault();
        this.performCheck();
      });

      if (this.formEl) {
        this.formEl.addEventListener('submit', (e) => {
          e.preventDefault();
          this.performCheck();
        });
      }

      // Allow only numbers and auto-limit to 6 digits
      this.inputEl.addEventListener('input', (e) => {
        const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
        if (e.target.value !== cleaned) {
          e.target.value = cleaned;
        }
      });

      // Enter key triggers check
      this.inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.performCheck();
        }
      });
    }

    loadSavedPincode() {
      try {
        const savedPin = localStorage.getItem(STORAGE_KEY);
        if (!savedPin || !/^[1-9][0-9]{5}$/.test(savedPin)) {
          return;
        }

        this.inputEl.value = savedPin;
        this.currentPincode = savedPin;

        const firstWord = this.getProductFirstWord();
        const cacheKey = `hor_delivery_cache_${firstWord}_${savedPin}`;
        const savedCacheStr = localStorage.getItem(cacheKey);

        if (savedCacheStr) {
          const cacheItem = JSON.parse(savedCacheStr);
          const isFresh =
            cacheItem &&
            cacheItem.timestamp &&
            Date.now() - cacheItem.timestamp < CLIENT_CACHE_TTL_MS;

          if (isFresh && cacheItem.data && cacheItem.pincode === savedPin) {
            this.renderSuccess(cacheItem.data);
            return;
          }
        }

        // If saved PIN exists but brand changed or cache expired, auto-check in background
        this.performCheck();
      } catch (err) {
        console.warn('LocalStorage access unavailable:', err);
      }
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.classList.add('hor-delivery-checker--loading');
        this.buttonEl.disabled = true;
        this.inputEl.disabled = true;
      } else {
        this.classList.remove('hor-delivery-checker--loading');
        this.buttonEl.disabled = false;
        this.inputEl.disabled = false;
      }
    }

    validatePincode(pincode) {
      if (!pincode || pincode.trim() === '') {
        return { valid: false, errorType: 'EMPTY', message: 'Please enter your PIN code.' };
      }
      const clean = pincode.trim();
      if (!/^[1-9][0-9]{5}$/.test(clean)) {
        return { valid: false, errorType: 'INVALID', message: 'Enter a valid 6-digit Indian PIN code.' };
      }
      return { valid: true, pincode: clean };
    }

    async performCheck() {
      const rawValue = this.inputEl.value;
      const validation = this.validatePincode(rawValue);

      if (!validation.valid) {
        this.renderError(validation.message, validation.errorType);
        return;
      }

      const pincode = validation.pincode;
      this.currentPincode = pincode;
      this.setLoading(true);

      let endpoint = this.dataset.endpoint || '/api/delivery-estimate';
      if (
        endpoint === '/api/delivery-estimate' &&
        (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost')
      ) {
        endpoint = 'http://localhost:3001/api/delivery-estimate';
      }
      const originPincode = this.dataset.originPincode || '492001';
      const productTitle = this.dataset.productTitle || document.querySelector('.product__title h1, .product__title')?.textContent?.trim() || '';
      const weight = this.dataset.weight || '0.8';
      const mrp = this.dataset.mrp || '4999';

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pincode,
            product_title: productTitle,
            origin_pincode: originPincode,
            weight_kg: weight,
            mrp
          })
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          if (response.status === 400) {
            this.renderError(errData.message || 'Enter a valid 6-digit Indian PIN code.', errData.error || 'INVALID');
          } else {
            this.renderError(errData.message || "We couldn't check delivery right now. Please try again.", 'TEMPORARY_UNAVAILABLE');
          }
          this.setLoading(false);
          return;
        }

        const data = await response.json();
        this.handleApiResponse(pincode, data);
      } catch (networkErr) {
        console.warn('Network error checking delivery estimate:', networkErr);
        // Fallback calculation for preview/offline mode when backend is unreachable
        this.handleOfflineFallback(pincode);
      } finally {
        this.setLoading(false);
      }
    }

    handleApiResponse(pincode, data) {
      if (!data.serviceable) {
        this.renderUnavailable(pincode, data.message, data.city);
        return;
      }

      if (!data.estimatedDateDisplay && !data.estimatedDays) {
        this.renderEstimateUnavailable(pincode);
        return;
      }

      // Save to localStorage segmented by product brand with 2h timestamp
      try {
        const firstWord = this.getProductFirstWord();
        const cacheKey = `hor_delivery_cache_${firstWord}_${pincode}`;
        const cacheItem = {
          data,
          pincode,
          firstWord,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, pincode);
        localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
      } catch (e) {}

      this.renderSuccess(data);
    }

    handleOfflineFallback(pincode) {
      // Calculate realistic client-side fallback date range
      const now = new Date();
      // Metro check: Mumbai (40), Delhi (11), Bangalore (56), Chennai (60), Kolkata (70), Hyderabad (50)
      const isMetro = ['11', '40', '56', '60', '70', '50'].some((prefix) => pincode.startsWith(prefix));
      const minDays = isMetro ? 2 : 4;
      const maxDays = isMetro ? 3 : 6;

      const startDate = new Date(now);
      startDate.setDate(startDate.getDate() + minDays);

      const endDate = new Date(now);
      endDate.setDate(endDate.getDate() + maxDays);

      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const startDay = startDate.getDate();
      const endDay = endDate.getDate();
      const startMonth = monthNames[startDate.getMonth()];
      const endMonth = monthNames[endDate.getMonth()];

      const dateDisplay = startMonth === endMonth
        ? `${startDay}–${endDay} ${startMonth}`
        : `${startDay} ${startMonth}–${endDay} ${endMonth}`;

      const fallbackData = {
        serviceable: true,
        pincode,
        estimatedDateDisplay: dateDisplay,
        estimatedDays: `${minDays} to ${maxDays} Days`,
        codAvailable: true,
        message: 'Delivery available'
      };

      try {
        const firstWord = this.getProductFirstWord();
        const cacheKey = `hor_delivery_cache_${firstWord}_${pincode}`;
        const cacheItem = {
          data: fallbackData,
          pincode,
          firstWord,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, pincode);
        localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
      } catch (e) {}

      this.renderSuccess(fallbackData);
    }

    renderSuccess(data) {
      const dateText = data.estimatedDateDisplay || data.estimatedDays || '3–5 Days';
      const locationText = data.city ? `${data.city} (${data.pincode})` : data.pincode;

      this.resultContainer.innerHTML = `
        <div class="hor-delivery-checker__result">
          <div class="hor-delivery-checker__status hor-delivery-checker__status--success">
            <svg class="hor-delivery-checker__status-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
            </svg>
            <span>Delivery available to <strong>${locationText}</strong></span>
            <button type="button" class="hor-delivery-checker__change-pincode" data-hor-change-btn>Change</button>
          </div>

          <div class="hor-delivery-checker__eta-wrapper">
            <span class="hor-delivery-checker__eta-label">Estimated Delivery:</span>
            <span class="hor-delivery-checker__eta-date">${dateText}</span>
          </div>

          <div class="hor-delivery-checker__notes">
            <div class="hor-delivery-checker__note-item">
              <span class="hor-delivery-checker__note-bullet">•</span>
              <span>Stringing &amp; personalization takes <strong>+1 day extra</strong></span>
            </div>
            <div class="hor-delivery-checker__note-item">
              <span class="hor-delivery-checker__note-bullet">•</span>
              <span>COD orders can be late by <strong>1 day</strong> vs prepaid</span>
            </div>
            <div class="hor-delivery-checker__note-disclaimer">
              *Estimated arrival — actual delivery depends on courier operations.
            </div>
          </div>
        </div>
      `;

      const changeBtn = this.resultContainer.querySelector('[data-hor-change-btn]');
      if (changeBtn) {
        changeBtn.addEventListener('click', () => {
          this.inputEl.focus();
          this.inputEl.select();
        });
      }
    }

    renderUnavailable(pincode, customMsg, city) {
      const msg = customMsg || "Sorry, delivery isn't currently available to this PIN code.";
      const locationText = city ? `${city} (${pincode})` : pincode;
      this.resultContainer.innerHTML = `
        <div class="hor-delivery-checker__result">
          <div class="hor-delivery-checker__status hor-delivery-checker__status--error">
            <svg class="hor-delivery-checker__status-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
            <span>Delivery unavailable for <strong>${locationText}</strong></span>
            <button type="button" class="hor-delivery-checker__change-pincode" data-hor-change-btn>Change</button>
          </div>
          <p class="hor-delivery-checker__error-msg">${msg}</p>
        </div>
      `;

      const changeBtn = this.resultContainer.querySelector('[data-hor-change-btn]');
      if (changeBtn) {
        changeBtn.addEventListener('click', () => {
          this.inputEl.focus();
          this.inputEl.select();
        });
      }
    }

    renderEstimateUnavailable(pincode) {
      this.resultContainer.innerHTML = `
        <div class="hor-delivery-checker__result">
          <div class="hor-delivery-checker__status hor-delivery-checker__status--warning">
            <svg class="hor-delivery-checker__status-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
            </svg>
            <span>Estimate unavailable</span>
            <button type="button" class="hor-delivery-checker__change-pincode" data-hor-change-btn>Change</button>
          </div>
          <p class="hor-delivery-checker__subtext">Delivery may be available, but we couldn't calculate an estimated date.</p>
        </div>
      `;

      const changeBtn = this.resultContainer.querySelector('[data-hor-change-btn]');
      if (changeBtn) {
        changeBtn.addEventListener('click', () => {
          this.inputEl.focus();
          this.inputEl.select();
        });
      }
    }

    renderError(message, errorType) {
      this.resultContainer.innerHTML = `
        <div class="hor-delivery-checker__result">
          <p class="hor-delivery-checker__error-msg">${message}</p>
        </div>
      `;
    }
  }

  if (!customElements.get('hor-delivery-checker')) {
    customElements.define('hor-delivery-checker', HorDeliveryChecker);
  }
})();
